/*
 * amountFormatting.ts
 * Formats amounts based on the user locale, and rounds to maximal 2 decimals if they exist.
 */

export const formatAmount = (amount: number, userLocale: string): string | null => {
    // Return an empty string or null for zero amount
    if (amount === 0) {
        return null;
    }

    // Round up the current amount
    const amountToFormat = Math.round(amount * 100) / 100;

    // Determine if the given amount is an integer
    const isInteger = Number.isInteger(amountToFormat);

    // Format the amount based on the user locale
    return new Intl.NumberFormat(userLocale, {
        // If the amount is an integer, show no decimal numbers, otherwise show minimal 1 decimal
        minimumFractionDigits: isInteger ? 0 : 1,

        // Show maximal 2 decimals if they exist
        maximumFractionDigits: 2
    }).format(amountToFormat);
};

// Formats a price string to the user locale with comma or period and always with 2 decimals
export const formatPriceString = (priceString: string | null, userLocale: string): string => {
    // Return an empty string or null if the price string is empty
    if (!priceString) {
        return '';
    }

    // Format the price
    return new Intl.NumberFormat(userLocale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(priceString));
};

// Formats a price string to the user locale with comma or period and always with 2 decimals. Also show the currency symbol
export const formatPriceCurrencyString = (priceString: string | null, userLocale: string, currency: string): string => {
    // Return an empty string or null if the price string is empty
    if (!priceString) {
        return '';
    }

    // Format the price with currency symbol
    return new Intl.NumberFormat(userLocale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(priceString));
};

// Formats an amount as a rounded currency string, for compact contexts like chart axes
export const formatCurrencyAmount = (amount: number, userLocale: string): string => {
    return new Intl.NumberFormat(userLocale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    }).format(amount);
};

// Formats a percentage on one decimal
export const formatPercentage = (value: number | string | null | undefined, userLocale: string) => {
    // Return if value is missing
    if (value === null || value === undefined) {
        return null;
    }

    // Convert to numeric value
    const numericValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;

    // Convert to string
    if (Number.isInteger(numericValue)) {
        return numericValue.toString();
    }

    // Format the percentage with one decimal
    return numericValue.toLocaleString(userLocale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
};